import {
    useCreateTransactionEntryRule,
    useUpdateTransactionEntryRule,
} from '@/components/cashflow/rules/useMutations';
import { Button } from '@/components/common/Button';
import { useToasts } from '@/components/common/Toasts';
import { useModal } from '@/components/ModalManager';
import { isApolloErrorWithCustomCode } from '@common/apollo';
import { TransactionEntryRulesDocument } from '@common/graphql/generated';
import { useRuleContext } from '@common/react/cashflow/rules';
import pluralize from '@common/util/pluralize';
import { isUndefined } from '@common/util/types';

const SubmitButton = () => {
    const modal = useModal();
    const { showErrorToast, showSuccessToast } = useToasts();

    const {
        isSubmitting,
        setIsSubmitting,
        isFormValid,
        enabledValidChanges,
        enabledValidConditions,
        rule,
        applyToAll,
        matchingTransactions,
        isGlobalRule,
    } = useRuleContext();

    const [createRule] = useCreateTransactionEntryRule(isGlobalRule);
    const [updateRule] = useUpdateTransactionEntryRule(isGlobalRule);

    return (
        <Button
            disabled={isSubmitting || !isFormValid}
            size="md"
            h={12}
            px={5}
            minW="115px"
            onClick={async () => {
                setIsSubmitting(true);

                try {
                    const variables = {
                        conditions: enabledValidConditions,
                        changeset: enabledValidChanges,
                        applyToAll,
                    };

                    if (rule) {
                        await updateRule({
                            variables: { id: rule.id, ...variables },
                        });
                    } else {
                        await createRule({
                            variables,
                            refetchQueries: [TransactionEntryRulesDocument],
                        });
                    }

                    let successMessage = `${isGlobalRule ? 'Global rule' : 'Rule'} successfully ${rule ? 'updated' : 'created'}.`;

                    if (applyToAll) {
                        if (isGlobalRule) {
                            successMessage += ` All matching transactions updated.`;
                        } else if (matchingTransactions?.length) {
                            successMessage += ` ${matchingTransactions.length} ${pluralize('transaction', matchingTransactions.length, false)} updated.`;
                        }
                    }

                    showSuccessToast(successMessage);

                    setIsSubmitting(false);
                    modal.hide();
                } catch (e) {
                    const errorMessage = getErrorMessage(e, isUndefined(rule));
                    showErrorToast(errorMessage);
                    setIsSubmitting(false);
                }
            }}
            onClickEvent={
                rule
                    ? 'Confirm Update Rule Button Clicked'
                    : 'Confirm Create Rule Button Clicked'
            }
        >
            {isSubmitting
                ? rule
                    ? 'Updating rule...'
                    : 'Creating rule...'
                : rule
                  ? 'Update rule'
                  : 'Create rule'}
        </Button>
    );
};

const getErrorMessage = (e: unknown, isNewRule: boolean) => {
    if (isApolloErrorWithCustomCode(e, 'USER_MERCHANT_FOR_GLOBAL_RULE')) {
        return 'Global rules cannot be created with user-created merchants. Please ensure that you are you using a global merchant in the changeset.';
    } else {
        return `There was an unexpected error ${isNewRule ? 'creating' : 'updating'} the rule.`;
    }
};

export default SubmitButton;
